// @flow

import React from 'react'
import { Link } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { classnames } from '@/helpers'
import styles from './styles.scss'

type ButtonProps = {|
  title?: string,
  primary?: boolean,
  onClick?: ?(event: DOMEvent) => void | string,
  ariaLabel: string,
  imgUrls?: ?{ default: string, selected: string },
  className?: string,
  slim?: boolean,
  disabled?: boolean,
|}

const ButtonComponent = (props: ButtonProps) => {
  const btnProps = { ...props }
  delete btnProps.primary // Delete unused non DOM prop
  delete btnProps.slim // Delete unused non DOM prop
  const { ariaLabel, onClick, title, imgUrls, className, ...rest } = btnProps
  const renderIcon = () => (imgUrls ? <span className="icon" /> : null)
  if (typeof onClick === 'string') {
    return (
      <Link
        to={onClick}
        onClick={e => e.stopPropagation()}
        aria-label={ariaLabel}
        className={classnames(
          styles.button,
          styles.link,
          className,
        )}
        {...rest}
      >
        {renderIcon()}
        {title}
      </Link>
    )
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={classnames(styles.button, className)}
      {...rest}
    >
      {renderIcon()}
      <div id="button_title">
        {title}
      </div>
    </button>
  )
}

const { orange1, orange2, orange3, boxShadowGrey, white } = styles
const { slimButtonHeight, inputHeight, standardFont, smallestFont, spacer, smallestSpacer, } = styles
const Button = styled(ButtonComponent)`
  font-size: ${standardFont};
  padding: 0 ${spacer};
  height: ${inputHeight};

  ${props =>
    props.slim &&
    css`
      height: ${slimButtonHeight};
      font-size: ${smallestFont};
      padding: 0 ${smallestSpacer};
    `}
  ${props =>
    !props.primary &&
    css`
      background: ${white} !important;
      border: 1px solid ${orange1} !important;
      color: ${orange3} !important;

      :hover {
        color: ${white};
        border: none;
      }
      :active {
        box-shadow: 0 8px 20px 0 ${boxShadowGrey};
        background-image: none;
        background-color: ${orange2};
        color: ${white};
      }
    `}
  ${props =>
    props.imgUrls &&
    css`
      display: flex;
      justify-content: space-evenly;
      align-items: center;
      :active {
        .icon {
          background-image: ${`url(${props.imgUrls.selected || props.imgUrls.default})`};
          background-repeat: no-repeat;
        }
      }
    `}
  .icon {
    background-image: ${props => (props.imgUrls ? `url(${props.imgUrls.default})` : 'none')};
    height: 17px;
    width: 17px;
    display: inline-block;
    background-repeat: no-repeat;
    margin: 5px 5px 0 0px;
  }
`
// Because we want to style an anchor and a button the easiest way to do that is to style the final component
// To receive default props we need to set them on `Button` but eslint looks for them on `ButtonComponent`
// We could apply default styles individual to `button` and `link` or use a function that returns default styles
// in a template literal. Flow doesn't like the later because it complains about functions being coerced to a string
// Managing duplicate props seems better then managing duplicate styles.
ButtonComponent.defaultProps = {
  imgUrls: null,
  primary: true,
  title: '',
  className: '',
  onClick: null,
  slim: false,
  disabled: false,
}

Button.defaultProps = {
  imgUrls: null,
  primary: true,
  title: '',
  className: '',
  onClick: null,
  slim: false,
  disabled: false,
}

export default Button
